import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, Grid, Badge } from '@mui/material';
import { DirectionsCar, Storefront, PersonOutline, Phone, LocationOn, Apartment, Email } from '@mui/icons-material';
import OrderPayment from './user.delivery.OrderPayment';
import OrderNotPayment from './user.delivery.OrderNotPayment';
import axios from 'axios';
import { useHistory } from 'react-router-dom';
import ComponentLoader from '../../customComponents/component.loader';

const OrderSummaryDetails = ({
    formData,
    cart,
    getDishPrice,
    getSelectedVariants,
    accountIdPayment,
    totalPrice,
    paymentOption,
    isOwner,
    getSumTotal,
    handlePayOptionChange,
    goBack,
    handleOrder,
    handlePay,
    loading,
    setLoading,
    ownerId,
    user,
    eraseUser,
    selectedCoupon,
    setSelectedCoupon
}) => {

    const [timeLeft, setTimeLeft] = useState(120);
    const [couponData, setCouponData] = useState(null)
    const history = useHistory();


    // 1 Decremento del timer
    useEffect(() => {
        const timer = setInterval(() => {
            setTimeLeft(prev => Math.max(prev - 1, 0));
        }, 1000);
        return () => clearInterval(timer);
    }, []); // nessuna dipendenza, parte solo all'inizio

    // 2 Azione quando il timer finisce
    useEffect(() => {
        if (timeLeft === 0) {
            goBack();
        }
    }, [timeLeft, goBack]); // dipende da timeLeft e dalla funzione goBack


    useEffect(() => {
        const fetchAndCompute = async () => {
            // 1. non per il gestore
            if (isOwner || !ownerId) {
                setLoading(false);
                return;
            }
            // 2. utente anonimo continua
            if (user.id === 'anonymus') {
                setLoading(false);
                return;
            }

            setLoading(true);
            try {
                // a) prendi il programma fedeltà
                const listRes = await axios.get('/api/user/fidelity/list/by-business', {
                    params: { businessId: ownerId }
                });
                if (!listRes.data.success || !listRes.data.result.length) {
                    throw new Error('Nessun programma fedeltà');
                }
                const prog = listRes.data.result[0];

                // b) prendi il summary per l'utente
                const summaryRes = await axios.post('/api/user/fidelity/summary', {
                    businessId: prog.ownerId,
                    data_inizio: prog.data_inizio,
                    data_fine: prog.data_fine,
                    data_inizio_fedelta: prog.data_inizio_fedelta,
                    data_fine_fedelta: prog.data_fine_fedelta,
                });
                if (!summaryRes.data.success) {
                    throw new Error('Errore recupero summary');
                }
                const s = summaryRes.data;

                // c) calcoli coupon
                const {
                    euro_spesi: euroSpesiPerCoupon = 0,
                    valore_coupon: valoreCoupon = 0,
                    ritiri_consegne: sogliaFedelta = 0,
                    valore_coupon_fedelta: valoreCouponFedelta = 0
                } = prog;

                // coupon spesa
                const totaleSpeso = s.totalePagato || 0;
                const fidelityUsed = s.couponFidelityUsed || 0;
                const couponTotali = euroSpesiPerCoupon > 0 ? Math.floor(totaleSpeso / euroSpesiPerCoupon) : 0;
                const couponDisponibili = Math.max(0, couponTotali - fidelityUsed);

                // coupon fedeltà
                const consegneCount = s.consegneCount || 0;
                const ritiriCount = s.ritiriCount || 0;
                const totaleOrdini = consegneCount + ritiriCount;
                const couponTotaliDelivery = sogliaFedelta > 0 ? Math.floor(totaleOrdini / sogliaFedelta) : 0;
                const deliveryUsed = s.couponDeliveryUsed || 0;
                const couponDisponibiliDelivery = Math.max(0, couponTotaliDelivery - deliveryUsed);

                const baseCouponData = {
                    program: prog,
                    summary: s,
                    couponSpesa: {
                        couponTotali,
                        couponDisponibili,
                        valoreCoupon,
                    },
                    couponFedelta: {
                        couponTotaliDelivery,
                        couponDisponibiliDelivery,
                        valoreCouponFedelta,
                    },
                };

                // d) solo se ci sono coupon disponibili, verifica se ce n'è uno già in uso
                if (couponDisponibili > 0 || couponDisponibiliDelivery > 0) {
                    try {
                        const openRes = await axios.post('/api/user/fidelity/open-coupons', {
                            businessId: ownerId
                        });

                        if (openRes.data?.success && openRes.data?.couponInUse) {
                            baseCouponData.openCoupons = openRes.data.couponInUse;
                        }
                    } catch (err) {
                        console.warn('Errore recupero open-coupons:', err);
                    }
                }

                setCouponData(baseCouponData);
            } catch (err) {
                console.error('Errore fidelity:', err);
                if (err.response?.status === 401) {
                    eraseUser();
                    history.push('/');
                }
            } finally {
                setLoading(false);
            }
        };

        fetchAndCompute();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOwner, ownerId, user]);


    return (
        <ComponentLoader isLoading={loading}>
            <div style={{ marginTop: '50px' }}>

                <Box mb={2} textAlign="center">
                    <Typography variant="body2" color="textSecondary">
                        Completa l’ordine entro {timeLeft} secondi
                    </Typography>
                </Box>
                <Box
                    p={3}
                    border="1px solid #ccc"
                    borderRadius={2}
                    boxShadow={2}
                    textAlign="left"
                    mb={3}
                >
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                        {formData.deliveryOption === 'Consegna' ? (
                            <DirectionsCar sx={{ color: '#FE7A36' }} />
                        ) : (
                            <Storefront sx={{ color: '#4CB9E7' }} />
                        )}
                        <Typography
                            variant="h6"
                            fontWeight="bold"
                            color={formData.deliveryOption === 'Consegna' ? '#FE7A36' : '#4CB9E7'}
                        >
                            {formData.deliveryOption}: {formData.deliveryTime}
                        </Typography>
                    </Box>

                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <PersonOutline color="action" />
                        <Typography variant="body1" fontWeight="bold">
                            {formData.name} {formData.surname}
                        </Typography>
                    </Box>

                    {formData.phoneNumber && (
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                            <Phone color="action" />
                            <Typography variant="body1" fontWeight="bold">
                                {formData.phoneNumber}
                            </Typography>
                        </Box>
                    )}

                    {formData.address && (
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                            <LocationOn color="action" />
                            <Typography variant="body1" fontWeight="bold">
                                {formData.address}
                            </Typography>
                        </Box>
                    )}

                    {formData.city && (
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                            <Apartment color="action" />
                            <Typography variant="body1" fontWeight="bold">
                                {formData.city}
                            </Typography>
                        </Box>
                    )}

                    {(formData.email && user.id === 'anonymus') && (
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                            <Email color="action" />
                            <Typography variant="body1" fontWeight="bold">
                                {formData.email}
                            </Typography>
                        </Box>
                    )}

                    {formData.noteDelivery && (
                        <Box mt={2}>
                            <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                                <strong>Note Consegna:</strong> {formData.noteDelivery}
                            </Typography>
                        </Box>
                    )}
                </Box>

                {/* Lista prodotti */}
                {cart.map((item, index) => (
                    <Paper
                        key={index}
                        elevation={3}
                        sx={{
                            p: 2,
                            mb: 3,
                            borderRadius: 2,
                        }}
                    >
                        <Grid container alignItems="center" spacing={2}>
                            <Grid item>
                                <Badge
                                    overlap="rectangular"
                                    color="primary"
                                    badgeContent={`x${item.amount}`}
                                    sx={{ mr: 1 }}
                                />
                            </Grid>

                            <Grid item xs>
                                <Typography variant="subtitle1" fontWeight="bold">
                                    {item.name}
                                </Typography>
                            </Grid>

                            <Grid item>
                                <Typography variant="subtitle1" fontWeight="bold">
                                    {(item.amount * getDishPrice(item)).toFixed(2).replace('.', ',')} €
                                </Typography>
                            </Grid>
                        </Grid>

                        {item.variants && item.variants.length > 0 && (
                            <Box mt={1}>
                                <div>{getSelectedVariants(item)}</div>
                            </Box>
                        )}

                        {item.note && (
                            <Box mt={2} p={1.5} borderRadius={1}>
                                <Typography variant="body2">
                                    <strong>Note:</strong> <em>{item.note}</em>
                                </Typography>
                            </Box>
                        )}
                    </Paper>
                ))}

                {accountIdPayment ? (
                    <OrderPayment
                        formData={formData}
                        totalPrice={totalPrice}
                        paymentOption={paymentOption}
                        isOwner={isOwner}
                        getSumTotal={getSumTotal}
                        handlePayOptionChange={handlePayOptionChange}
                        goBack={goBack}
                        handleOrder={handleOrder}
                        handlePay={handlePay}
                        loading={loading}
                        user={user}
                    />
                ) : (
                    <OrderNotPayment
                        formData={formData}
                        totalPrice={totalPrice}
                        getSumTotal={getSumTotal}
                        goBack={goBack}
                        handleOrder={handleOrder}
                        loading={loading}
                        couponData={couponData}
                        onSelectCoupon={setSelectedCoupon}
                        selectedCoupon={selectedCoupon}
                        user={user}
                    />
                )}
            </div>
        </ComponentLoader>
    );
};

export default OrderSummaryDetails;
